import {
  API_PREFIX_CATEGORY_PATH,
  API_PREFIX_MENU_PREFERENCE_PATH,
  API_PREFIX_PAGE_PATH,
  API_PREFIX_POST_PATH,
} from "@/constants/prefix-path-api"
import { message } from "antd"
import { HttpClient } from "../../../../app/api/HttpClient"
import { t } from "../../../../languages/i18n"
import { FilterSearchParams } from "../../../../lib/external-components"
import type { MenuPreferencePayload, MenuSourceItem, MenuSourceType } from "../types/menu"

type MenuListQueryParams = Omit<FilterSearchParams, "populate"> & {
  fields: string[]
}

/**
 * `menu-preferences` không có trường `domain` (xem sitemap-services.ts), nên mọi
 * request tới nó phải bỏ qua bộ lọc domain tự động của AdminHttpClient.
 */
const MENU_REQUEST_OPTIONS = { skipDomainFilter: true } as const

const buildMenuListParams = (fields: string[]): MenuListQueryParams => ({
  fields,
  sort: "createdAt:desc",
  publicationState: "preview",
  filters: {},
  pagination: {
    pageSize: "100",
  },
  locale: "all",
})

interface MenuReferenceRecord {
  id?: number
  attributes?: {
    name?: string | null
    title?: string | null
    slug?: string | null
    locale?: string | null
  }
}

/**
 * Gom bản ghi Strapi về dạng nguồn hiển thị trong panel "Thêm mục".
 * Khoá phải theo id: cùng một trang ở nhiều ngôn ngữ dùng chung slug, khoá theo
 * slug sẽ khiến hai dòng trùng khoá và tick ô này lại tick luôn ô kia.
 */
const toSourceItems = (data: MenuReferenceRecord[] | undefined, type: MenuSourceType): MenuSourceItem[] =>
  (data || [])
    .map(record => {
      const name = record.attributes?.name?.trim() || record.attributes?.title?.trim() || ""
      const slug = record.attributes?.slug?.trim() || ""
      if (!name || record.id === undefined) return null
      return {
        key: `${type}:${record.id}`,
        name,
        slug: slug || "#",
        type,
        locale: record.attributes?.locale || "",
      }
    })
    .filter((item): item is MenuSourceItem => Boolean(item))

const fetchAllPages = async (searchTerms: { [key: string]: string } = {}) => {
  const params: MenuListQueryParams = buildMenuListParams(["name", "title", "slug", "locale"])

  const excludedKeys = ["current", "pageSize", "start", "limit", "total", "page"]

  Object.keys(searchTerms).forEach(key => {
    if (!excludedKeys.includes(key)) {
      const value = searchTerms[key]

      if (key === "publicationState") {
        const convertParamStatusPost = value === "draft" || value === "all" ? "preview" : "live"
        params.publicationState = convertParamStatusPost

        if (value === "draft") {
          params.filters.publishedAt = { $null: true }
        } else {
          delete params.filters.publishedAt
        }
      } else if (key === "locale") {
        params.locale = value
      } else if (!excludedKeys.includes(key)) {
        if (value) {
          params.filters[key] = { $containsi: value }
        }
      } else if (value) {
        params.pagination = {
          ...params.pagination,
          [key]: value,
        }
      }
    } else {
      const value = searchTerms[key]
      if (value) {
        params.pagination = {
          ...params.pagination,
          [key]: value,
        }
      }
    }
  })

  try {
    const response = await HttpClient.get(`${API_PREFIX_PAGE_PATH}`, { params })

    return {
      data: response.data,
      meta: response.meta,
    }
  } catch (error) {
    message.error(t("Lỗi khi tải danh sách trang. Vui lòng thử lại."))
  }
}

const fetchAllPost = async () => {
  try {
    const response = await HttpClient.get(`${API_PREFIX_POST_PATH}`, {
      params: buildMenuListParams(["name", "slug", "locale"]),
    })
    return response
  } catch (error) {
    message.error(t("Lỗi khi tải danh sách bài viết. Vui lòng thử lại."))
  }
}

const fetchAllBases = async () => {
  try {
    const response = await HttpClient.get(`${API_PREFIX_MENU_PREFERENCE_PATH}`, MENU_REQUEST_OPTIONS)

    return response
  } catch (error) {
    message.error(t("Lỗi khi tải danh sách trang cơ sở. Vui lòng thử lại."))
  }
}

const fetchAllCategories = async () => {
  try {
    const response = await HttpClient.get(`${API_PREFIX_CATEGORY_PATH}`, {
      params: buildMenuListParams(["name", "slug", "locale"]),
    })

    return response
  } catch (error) {
    message.error(t("Lỗi khi tải danh sách danh mục. Vui lòng thử lại."))
  }
}

/** Nạp nguồn cho panel "Thêm mục" — trang, bài viết và danh mục ở mọi ngôn ngữ. */
const fetchMenuSources = async (): Promise<MenuSourceItem[]> => {
  const [pages, posts, categories] = await Promise.all([fetchAllPages(), fetchAllPost(), fetchAllCategories()])

  return [
    ...toSourceItems(pages?.data, "page"),
    ...toSourceItems(posts?.data, "post"),
    ...toSourceItems(categories?.data, "category"),
  ]
}

const fetchAllMenuPreference = async () => {
  try {
    const response = await HttpClient.get(`${API_PREFIX_MENU_PREFERENCE_PATH}`, {
      params: {
        fields: ["name", "items", "locale"],
        sort: "createdAt:desc",
        pagination: {
          pageSize: "100",
        },
        locale: "all",
      },
      ...MENU_REQUEST_OPTIONS,
    })
    return response
  } catch (error) {
    message.error(t("Lỗi khi tải thiết lập menu. Vui lòng thử lại."))
  }
}

export interface MenuQueryConfig {
  fields?: string[]
  populate?: string | Record<string, any> | null
}

/** Danh sách menu cho trang /admin/menus — có tìm kiếm, lọc ngôn ngữ, phân trang. */
const fetchMenuPreferences = async (
  searchTerms: { [key: string]: string } = {},
  queryConfig: MenuQueryConfig = {},
) => {
  const params: FilterSearchParams & { fields: string[] } = {
    fields: queryConfig.fields?.length ? queryConfig.fields : ["name", "items", "locale", "createdAt", "updatedAt"],
    sort: searchTerms.sort || "createdAt:desc",
    filters: {},
    pagination: {
      page: searchTerms.page || "1",
      pageSize: searchTerms.pageSize || "20",
    },
    locale: searchTerms.locale && searchTerms.locale !== "all" ? searchTerms.locale : "all",
  }

  if (searchTerms.name) {
    params.filters.name = { $containsi: searchTerms.name }
  }

  try {
    return await HttpClient.get(`${API_PREFIX_MENU_PREFERENCE_PATH}`, { params, ...MENU_REQUEST_OPTIONS })
  } catch (error) {
    message.error(t("Lỗi khi tải danh sách menu. Vui lòng thử lại."))
  }
}

const getMenuPreferenceById = async (id: string | number) => {
  const response = await HttpClient.get(`${API_PREFIX_MENU_PREFERENCE_PATH}/${id}`, {
    params: { fields: ["name", "items", "locale", "createdAt", "updatedAt"] },
    ...MENU_REQUEST_OPTIONS,
  })
  return response?.data
}

const createMenuPreference = async (data: MenuPreferencePayload) =>
  HttpClient.post(`${API_PREFIX_MENU_PREFERENCE_PATH}`, { data }, MENU_REQUEST_OPTIONS)

const updateMenuPreference = async (id: string | number, data: MenuPreferencePayload) =>
  HttpClient.put(`${API_PREFIX_MENU_PREFERENCE_PATH}/${id}`, { data }, MENU_REQUEST_OPTIONS)

const deleteMenuPreference = async (id: string | number) =>
  HttpClient.delete(`${API_PREFIX_MENU_PREFERENCE_PATH}/${id}`, MENU_REQUEST_OPTIONS)

/** Nhân bản menu: giữ nguyên cấu trúc items, đổi tên để phân biệt. */
const duplicateMenuPreference = async (id: string | number) => {
  const original = await getMenuPreferenceById(id)
  const attributes = original?.attributes

  if (!attributes?.name) {
    throw new Error("Invalid menu data")
  }

  return createMenuPreference({
    name: `${attributes.name} (copy)`,
    items: Array.isArray(attributes.items) ? attributes.items : [],
    locale: attributes.locale || "vi",
  })
}

const menuService = {
  fetchAllPages,
  fetchAllPost,
  fetchAllCategories,
  fetchAllBases,
  fetchAllMenuPreference,
  fetchMenuSources,
  fetchMenuPreferences,
  getMenuPreferenceById,
  createMenuPreference,
  updateMenuPreference,
  deleteMenuPreference,
  duplicateMenuPreference,
}

export default menuService
